import { create } from "zustand"
import api from "../services/api"
import getUserProfileValidate from "../helpers/getUserProfileValidate"
import { canais } from "../utils/canais"
import {
  mesclarTicket,
  mesclarContatoEmTicket,
  ticketsRemoverFilaPorId,
  filasUsuarioPermitidasCalcular,
  usuarioTemAcessoAoTicket,
  ticketPassaNosFiltros,
  addIdToTop,
  removeId,
  sortIdsByRecent,
  mergeIdsForPage,
  calcularMensagensNaoLidas,
} from "../helpers/ticketsStoreHelpers"
import { conversaEhSemResponsavelAtiva } from "../helpers/conversaSemResponsavel"
import { useKanbanTicketsStore } from "./kanbanTicketsStore"
import useUsuariosPermitidosStore from "./usuariosPermitidosStore"
import useContactsStore from "./ContactsStore"

// Converte body+mediaType em texto de preview para a lista de tickets
const REGEX_ASSINATURA_TEXTO = /^\*[^*\n]+:\*\s*/u

const mensagemTextoSemAssinatura = (cTexto) => cTexto.replace(REGEX_ASSINATURA_TEXTO, "").trim()

const obterUltimaMensagemPreview = (oMessage) => {
  const cBody = typeof oMessage?.body === "string" ? oMessage.body.trim() : ""
  const cTextoUtil = mensagemTextoSemAssinatura(cBody)
  const cMediaType = oMessage?.mediaType

  if (oMessage?.isDeleted || cBody === "_INFO_MESSAGE_DELETED_INFO_") {
    return "_INFO_MESSAGE_DELETED_INFO_"
  }

  // Placeholder já formatado (API / banco)
  if (/^_INFO_.+_INFO_$/.test(cBody)) {
    return cBody
  }

  // Mídia sem legenda útil (vazio ou só assinatura *Nome:*): usa placeholder do card
  if (cMediaType === "image" && !cTextoUtil) return "_INFO_IMAGE_INFO_"
  if (cMediaType === "image") return cTextoUtil || "_INFO_IMAGE_INFO_"
  if (cMediaType === "audio" && !cTextoUtil) return "_INFO_AUDIO_INFO_"
  if (cMediaType === "audio") return cTextoUtil || "_INFO_AUDIO_INFO_"
  if (cMediaType === "video" && !cTextoUtil) return "_INFO_VIDEO_INFO_"
  if (cMediaType === "video") return cTextoUtil || "_INFO_VIDEO_INFO_"
  if (cMediaType === "locationMessage" || cMediaType === "liveLocationMessage") {
    return "_INFO_LOCATIONMESSAGE_INFO_"
  }
  if (cMediaType === "contactMessage" || cMediaType === "contactsArrayMessage") {
    return "_INFO_CONTACTMESSAGE_INFO_"
  }
  if (cBody.startsWith("data:image/")) {
    return "_INFO_LOCATIONMESSAGE_INFO_"
  }
  if (/^BEGIN:VCARD/i.test(cTextoUtil)) {
    return "_INFO_CONTACTMESSAGE_INFO_"
  }
  // Arquivo / documento (e demais mídias) sem legenda útil
  if (cMediaType && !cTextoUtil) return "_INFO_APPLICATION_INFO_"
  if (cMediaType && cTextoUtil) return cTextoUtil
  // Texto normal — ignora marcadores internos
  if (cBody && !cBody.startsWith("_INFO_")) return cBody

  return ""
}

const initialState = {
  ticketsById: {},
  openIds: [],
  pendingIds: [],
  mNaoLidasIds: [],
  counters: { open: 0, unread: 0, pending: 0 },
  pagination: {
    open: { page: 1, hasMore: true },
    pending: { page: 1, hasMore: true },
    unread: { page: 1, hasMore: true },
  },
  loading: { open: false, pending: false, unread: false },
  lastFetchedAt: { open: null, pending: null, unread: null },
  socketConnected: false,
  socketCleanup: null,
  socketInfo: { companyId: null, user: null },
  /** companyId:userId da última carga inicial bem-sucedida (evita cache entre sessões). */
  cChaveSessaoInicial: null,
  /** Filtros apenas da lista de conversas (TicketsManager / abas abertas + sem responsável). */
  filterContextListaConversas: {
    oFilasSelecionadas: [],
    oSituacoesSelecionadas: [],
    oUsuariosSelecionados: [],
    oContatosSelecionados: [],
    oMotivosSelecionados: [],
    canalSelecionado: null,
    cSearchParam: "",
    oFilasDoUserLogado: [],
    isUserPermitted: () => false,
  },
}

let nFetchInicialGeracao = 0
/** Deduplica chamadas simultâneas à página 1 (login prefetch + mount do componente). */
let oBuscaInicialPagina1Pendente = null
let cBuscaInicialParametrosPendentes = null

const ticketPassaFiltrosLista = (ticket, state, { ehSemResponsavel = false } = {}) => {
  const oCtx = state.filterContextListaConversas
  const user = state.socketInfo.user
  if (!ticket || !user) return false

  return ticketPassaNosFiltros({
    ticket,
    filtros: oCtx,
    ehSemResponsavel,
    user,
    oFilasDoUserLogado: oCtx.oFilasDoUserLogado,
    isUserPermitted: oCtx.isUserPermitted,
  })
}

export const useTicketsStore = create((set, get) => ({
  ...initialState,

  resetStore: () => {
    nFetchInicialGeracao = 0
    oBuscaInicialPagina1Pendente = null
    cBuscaInicialParametrosPendentes = null
    const fLimpezaSocket = get().socketCleanup
    if (typeof fLimpezaSocket === "function") fLimpezaSocket()
    useKanbanTicketsStore.getState().resetarKanbanStore()
    set({ ...initialState })
  },

  setFilterContextLista: (oParcial) => {
    set((state) => {
      const filterContextListaConversas = { ...state.filterContextListaConversas, ...oParcial }
      const oEstadoComContexto = { ...state, filterContextListaConversas }

      const openIds = state.openIds.filter((id) => {
        const oTicket = state.ticketsById[id]
        return (
          oTicket &&
          oTicket.status === "open" &&
          oTicket.responsavelId != null &&
          ticketPassaFiltrosLista(oTicket, oEstadoComContexto)
        )
      })

      const pendingIds = state.pendingIds.filter((id) => {
        const oTicket = state.ticketsById[id]
        return oTicket && conversaEhSemResponsavelAtiva(oTicket) && ticketPassaFiltrosLista(oTicket, oEstadoComContexto, { ehSemResponsavel: true })
      })

      const mNaoLidasIds = state.mNaoLidasIds.filter((id) => {
        const oTicket = state.ticketsById[id]
        return (
          oTicket &&
          oTicket.status === "open" &&
          calcularMensagensNaoLidas(oTicket, state.socketInfo.user?.id) > 0 &&
          ticketPassaFiltrosLista(oTicket, oEstadoComContexto)
        )
      })

      return { filterContextListaConversas, openIds, pendingIds, mNaoLidasIds }
    })
  },

  upsertTicket: (oTicket) => {
    if (!oTicket?.id) return
    const { user } = get().socketInfo
    const nUsuarioId = user?.id

    set((state) => {
      const oldTicket = state.ticketsById[oTicket.id]
      const mergedTicket = mesclarTicket(oldTicket, oTicket, nUsuarioId)
      const ticketsById = { ...state.ticketsById, [oTicket.id]: mergedTicket }

      let openIds = state.openIds
      let pendingIds = state.pendingIds
      let mNaoLidasIds = state.mNaoLidasIds

      const lPassaFiltrosAbertos = ticketPassaFiltrosLista(mergedTicket, state)
      const lPassaFiltrosSemResponsavel = ticketPassaFiltrosLista(mergedTicket, state, { ehSemResponsavel: true })

      if (mergedTicket.status === "open" && mergedTicket.responsavelId != null && lPassaFiltrosAbertos) {
        openIds = addIdToTop(openIds, mergedTicket.id)
      } else {
        openIds = removeId(openIds, mergedTicket.id)
      }

      if (conversaEhSemResponsavelAtiva(mergedTicket) && lPassaFiltrosSemResponsavel) {
        pendingIds = addIdToTop(pendingIds, mergedTicket.id)
      } else {
        pendingIds = removeId(pendingIds, mergedTicket.id)
      }

      const lEraNaoLida =
        Boolean(oldTicket) && oldTicket.status === "open" && calcularMensagensNaoLidas(oldTicket, nUsuarioId) > 0
      const lEhNaoLida =
        mergedTicket.status === "open" &&
        calcularMensagensNaoLidas(mergedTicket, nUsuarioId) > 0 &&
        lPassaFiltrosAbertos

      if (lEhNaoLida) {
        mNaoLidasIds = addIdToTop(mNaoLidasIds, mergedTicket.id)
      } else {
        mNaoLidasIds = removeId(mNaoLidasIds, mergedTicket.id)
      }

      openIds = sortIdsByRecent(openIds, ticketsById)
      pendingIds = sortIdsByRecent(pendingIds, ticketsById)
      mNaoLidasIds = sortIdsByRecent(mNaoLidasIds, ticketsById)

      const lEraOpen = Boolean(oldTicket) && oldTicket.status === "open"
      const lEhOpen = mergedTicket.status === "open"
      const lEraPending = Boolean(oldTicket) && conversaEhSemResponsavelAtiva(oldTicket)
      const lEhPending = conversaEhSemResponsavelAtiva(mergedTicket)

      let nContadorOpen = state.counters.open
      if (lEraOpen !== lEhOpen) {
        if (lEhOpen) nContadorOpen += 1
        else nContadorOpen = Math.max(0, nContadorOpen - 1)
      }

      let nContadorPending = state.counters.pending
      if (state.lastFetchedAt.pending != null && !state.pagination.pending.hasMore) {
        nContadorPending = pendingIds.length
      } else if (lEraPending !== lEhPending) {
        if (lEhPending) nContadorPending += 1
        else nContadorPending = Math.max(0, nContadorPending - 1)
      }

      let nContadorUnread = state.counters.unread
      if (state.lastFetchedAt.unread != null && !state.pagination.unread.hasMore) {
        nContadorUnread = mNaoLidasIds.length
      } else if (lEraNaoLida !== lEhNaoLida) {
        if (lEhNaoLida) nContadorUnread += 1
        else nContadorUnread = Math.max(0, nContadorUnread - 1)
      }

      return {
        ticketsById,
        openIds,
        pendingIds,
        mNaoLidasIds,
        counters: {
          ...state.counters,
          open: nContadorOpen,
          pending: nContadorPending,
          unread: nContadorUnread,
        },
      }
    })

    const merged = get().ticketsById[oTicket.id]
    if (merged) {
      useKanbanTicketsStore.getState().mesclarTicketDaListaSocket(merged, nUsuarioId)
    }
  },

  /**
   * Após exclusão de fila: zera queueId/queue nos tickets em memória (lista + Kanban).
   */
  removerFilaDosTickets: (nFilaId) => {
    if (nFilaId == null) return
    set((state) => {
      const ticketsById = ticketsRemoverFilaPorId(state.ticketsById, nFilaId)
      if (ticketsById === state.ticketsById) return {}
      return { ticketsById }
    })
    useKanbanTicketsStore.getState().removerFilaDosTickets(nFilaId)
  },

  removeTicket: (nTicketId) => {
    if (!nTicketId) return
    const removedTicket = get().ticketsById[nTicketId]
    set((state) => {
      const ticketsById = { ...state.ticketsById }
      delete ticketsById[nTicketId]

      const openIds = removeId(state.openIds, nTicketId)
      const pendingIds = removeId(state.pendingIds, nTicketId)
      const mNaoLidasIds = removeId(state.mNaoLidasIds, nTicketId)
      const nUsuarioId = state.socketInfo.user?.id
      const lEraNaoLida =
        removedTicket && removedTicket.status === "open" && calcularMensagensNaoLidas(removedTicket, nUsuarioId) > 0
      const lEraOpen = removedTicket?.status === "open"
      const lEraPending = removedTicket && conversaEhSemResponsavelAtiva(removedTicket)

      let nContadorOpen = state.counters.open
      if (lEraOpen) nContadorOpen = Math.max(0, nContadorOpen - 1)

      let nContadorPending = state.counters.pending
      if (state.lastFetchedAt.pending != null && !state.pagination.pending.hasMore) {
        nContadorPending = pendingIds.length
      } else if (lEraPending) {
        nContadorPending = Math.max(0, nContadorPending - 1)
      }

      let nContadorUnread = state.counters.unread
      if (state.lastFetchedAt.unread != null && !state.pagination.unread.hasMore) {
        nContadorUnread = mNaoLidasIds.length
      } else if (lEraNaoLida) {
        nContadorUnread = Math.max(0, nContadorUnread - 1)
      }

      return {
        ticketsById,
        openIds,
        pendingIds,
        mNaoLidasIds,
        counters: {
          ...state.counters,
          open: nContadorOpen,
          pending: nContadorPending,
          unread: nContadorUnread,
        },
      }
    })
    useKanbanTicketsStore.getState().removerTicketDaListaSocket(nTicketId, removedTicket)
  },

  updateTicketUnread: (nTicketId, oTicket) => {
    if (!oTicket) return
    get().upsertTicket({ ...oTicket, id: nTicketId || oTicket.id })
  },

  /**
   * Reflete a edição de um contato na lista de conversas em tempo real, sem refetch.
   * Atualiza ticket.contact de todos os tickets que referenciam o contato editado.
   */
  atualizarContatoNosTickets: (oContato) => {
    if (!oContato?.id) return
    set((state) => {
      let lAlterou = false
      const ticketsById = { ...state.ticketsById }
      Object.keys(ticketsById).forEach((cId) => {
        const oTicket = ticketsById[cId]
        const oTicketAtualizado = mesclarContatoEmTicket(oTicket, oContato)
        if (oTicketAtualizado !== oTicket) {
          ticketsById[cId] = oTicketAtualizado
          lAlterou = true
        }
      })
      if (!lAlterou) return state
      return { ticketsById }
    })
  },

  handleNewMessage: (message) => {
    const ticket = message?.ticket
    if (!ticket?.id) return

    const { user } = get().socketInfo.user ? { user: get().socketInfo.user } : { user: null }
    const oFilasDoUserLogado = filasUsuarioPermitidasCalcular(user)
    const isUserPermitted = useUsuariosPermitidosStore.getState().isUserPermitted

    // Mescla ticket do socket com o existente para não perder campos (ex: queueId, responsavelId)
    const oTicketExistente = get().ticketsById?.[ticket.id]
    const ticketParaValidar = oTicketExistente ? mesclarTicket(oTicketExistente, ticket, user?.id) : ticket

    // Verifica acesso usando o ticket completo (não o payload parcial do socket)
    if (
      !usuarioTemAcessoAoTicket({
        ticket: ticketParaValidar,
        user,
        oFilasSelecionadas: [],
        oFilasDoUserLogado,
        profile: user?.profile,
        isUserPermitted,
      })
    ) {
      return
    }

    // Preview da mensagem nova tem prioridade sobre o campo do ticket (que pode ser o valor antigo)
    const cPreviewNovaMensagem = obterUltimaMensagemPreview(message)
    const cDataNovaMensagem =
      message.updatedAt || message.createdAt || ticket?.ultimaMensagemData || ticket?.updatedAt || ticket?.createdAt

    const processedTicket =
      user && getUserProfileValidate(user, { message, ticket: ticketParaValidar })
        ? {
            ...ticketParaValidar,
            ultimaMensagem: cPreviewNovaMensagem || ticket?.ultimaMensagem || "",
            ultimaMensagemData: cDataNovaMensagem,
          }
        : {
            ...ticketParaValidar,
            ultimaMensagem: "_INFO_MESSAGE_RESTRICTED_INFO_",
            ultimaMensagemData: cDataNovaMensagem,
          }

    get().upsertTicket(processedTicket)
  },

  ticketsInicialBuscar: async (oParametros = {}, nPagina = 1, oOpcoes = {}) => {
    const lForcar = Boolean(oOpcoes?.forcar)
    const lSilencioso = Boolean(oOpcoes?.silencioso)

    const cChaveParametros = JSON.stringify(oParametros)
    if (
      nPagina === 1 &&
      !lForcar &&
      oBuscaInicialPagina1Pendente &&
      cBuscaInicialParametrosPendentes === cChaveParametros
    ) {
      return oBuscaInicialPagina1Pendente
    }

    const executarBusca = async () => {
      const nGeracao = ++nFetchInicialGeracao
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, open: true } }))
      }
      try {
        const { data: oResposta } = await api.get("/tickets/inicial", {
          params: {
            ...oParametros,
            pagina: nPagina,
          },
        })

        if (nGeracao !== nFetchInicialGeracao) {
          if (!lSilencioso) {
            set((state) => ({ loading: { ...state.loading, open: false } }))
          }
          return
        }

        const mTickets = Array.isArray(oResposta?.tickets) ? oResposta.tickets : []
        const oContagens = oResposta?.contagens || {}

        set((state) => {
        let ticketsById = { ...state.ticketsById }
        const mIdsAbertosBase = nPagina === 1 ? [] : [...state.openIds]

        mTickets.forEach((oTicket) => {
          const oMesclado = mesclarTicket(ticketsById[oTicket.id], oTicket, state.socketInfo.user?.id)
          ticketsById[oTicket.id] = oMesclado
        })

        const openIds = sortIdsByRecent(mergeIdsForPage(mIdsAbertosBase, mTickets, nPagina), ticketsById)

        return {
          ticketsById,
          openIds,
          pendingIds: nPagina === 1 ? [] : state.pendingIds,
          mNaoLidasIds: nPagina === 1 ? [] : state.mNaoLidasIds,
          counters: {
            open: Number(oContagens.nAbertos ?? oResposta?.count ?? openIds.length),
            pending: Number(oContagens.nSemResponsavel ?? state.counters.pending),
            unread: Number(oContagens.nNaoLidas ?? state.counters.unread),
          },
          pagination: {
            ...state.pagination,
            open: { page: nPagina, hasMore: Boolean(oResposta?.hasMore) },
            pending: nPagina === 1 ? { page: 1, hasMore: true } : state.pagination.pending,
            unread: nPagina === 1 ? { page: 1, hasMore: true } : state.pagination.unread,
          },
          loading: { ...state.loading, open: lSilencioso ? state.loading.open : false },
          lastFetchedAt: {
            ...state.lastFetchedAt,
            open: Date.now(),
            pending: nPagina === 1 ? null : state.lastFetchedAt.pending,
            unread: nPagina === 1 ? null : state.lastFetchedAt.unread,
          },
          cChaveSessaoInicial:
            nPagina === 1
              ? `${localStorage.getItem("companyId")}:${localStorage.getItem("userId")}`
              : state.cChaveSessaoInicial,
        }
        })
      } catch (oErro) {
        if (nGeracao === nFetchInicialGeracao && !lSilencioso) {
          set((state) => ({ loading: { ...state.loading, open: false } }))
        }
        if (nGeracao === nFetchInicialGeracao) {
          throw oErro
        }
      }
    }

    const oPromessa = executarBusca()
    if (nPagina === 1 && !lForcar) {
      oBuscaInicialPagina1Pendente = oPromessa
      cBuscaInicialParametrosPendentes = cChaveParametros
      oPromessa.finally(() => {
        if (oBuscaInicialPagina1Pendente === oPromessa) {
          oBuscaInicialPagina1Pendente = null
          cBuscaInicialParametrosPendentes = null
        }
      })
    }
    return oPromessa
  },

  obterChaveSessaoInicial: () => `${localStorage.getItem("companyId")}:${localStorage.getItem("userId")}`,

  fetchOpenTickets: async (params = {}, page = 1, options = {}) => {
    const lSilencioso = Boolean(options?.silencioso ?? options?.silent)
    if (!lSilencioso) {
      set((state) => ({ loading: { ...state.loading, open: true } }))
    }
    try {
      const { data } = await api.get("/tickets", {
        params: {
          ...params,
          status: "open",
          pagina: page,
        },
      })

      const tickets = Array.isArray(data?.tickets) ? data.tickets : []
      set((state) => {
        let ticketsById = { ...state.ticketsById }
        const openIdsBase = page === 1 ? [] : [...state.openIds]

        tickets.forEach((t) => {
          const merged = mesclarTicket(ticketsById[t.id], t, state.socketInfo.user?.id)
          ticketsById[t.id] = merged
        })

        const openIds = sortIdsByRecent(mergeIdsForPage(openIdsBase, tickets, page), ticketsById)

        return {
          ticketsById,
          openIds,
          counters: {
            ...state.counters,
            open: Number(data?.count ?? openIds.length),
          },
          pagination: {
            ...state.pagination,
            open: { page, hasMore: Boolean(data?.hasMore) },
          },
          loading: { ...state.loading, open: lSilencioso ? state.loading.open : false },
          lastFetchedAt: { ...state.lastFetchedAt, open: Date.now() },
        }
      })
    } catch (error) {
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, open: false } }))
      }
      throw error
    }
  },

  fetchPendingTickets: async (params = {}, page = 1, options = {}) => {
    const lSilencioso = Boolean(options?.silencioso ?? options?.silent)
    if (!lSilencioso) {
      set((state) => ({ loading: { ...state.loading, pending: true } }))
    }
    try {
      const { data } = await api.get("/tickets", {
        params: {
          ...params,
          semResponsavel: "true",
          pagina: page,
        },
      })

      const tickets = Array.isArray(data?.tickets) ? data.tickets : []
      set((state) => {
        let ticketsById = { ...state.ticketsById }
        const pendingIdsBase = page === 1 ? [] : [...state.pendingIds]

        tickets.forEach((t) => {
          const merged = mesclarTicket(ticketsById[t.id], t, state.socketInfo.user?.id)
          ticketsById[t.id] = merged
        })

        const pendingIds = sortIdsByRecent(mergeIdsForPage(pendingIdsBase, tickets, page), ticketsById)

        const lTemMaisPending = Boolean(data?.hasMore)
        return {
          ticketsById,
          pendingIds,
          counters: {
            ...state.counters,
            pending: Number(data?.count ?? state.counters.pending),
          },
          pagination: {
            ...state.pagination,
            pending: { page, hasMore: lTemMaisPending },
          },
          loading: { ...state.loading, pending: lSilencioso ? state.loading.pending : false },
          lastFetchedAt: { ...state.lastFetchedAt, pending: Date.now() },
        }
      })
    } catch (error) {
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, pending: false } }))
      }
      throw error
    }
  },

  fetchConversasNaoLidas: async (params = {}, page = 1, options = {}) => {
    const lSilencioso = Boolean(options?.silencioso ?? options?.silent)
    if (!lSilencioso) {
      set((state) => ({ loading: { ...state.loading, unread: true } }))
    }
    try {
      const { data } = await api.get("/tickets", {
        params: {
          ...params,
          status: "open",
          apenasNaoLidas: "true",
          pagina: page,
        },
      })

      const tickets = Array.isArray(data?.tickets) ? data.tickets : []
      set((state) => {
        let ticketsById = { ...state.ticketsById }
        const mNaoLidasIdsBase = page === 1 ? [] : [...state.mNaoLidasIds]

        tickets.forEach((t) => {
          const merged = mesclarTicket(ticketsById[t.id], t, state.socketInfo.user?.id)
          ticketsById[t.id] = merged
        })

        const mNaoLidasIds = sortIdsByRecent(mergeIdsForPage(mNaoLidasIdsBase, tickets, page), ticketsById)

        return {
          ticketsById,
          mNaoLidasIds,
          counters: {
            ...state.counters,
            unread: Number(data?.count ?? mNaoLidasIds.length),
          },
          pagination: {
            ...state.pagination,
            unread: { page, hasMore: Boolean(data?.hasMore) },
          },
          loading: { ...state.loading, unread: lSilencioso ? state.loading.unread : false },
          lastFetchedAt: { ...state.lastFetchedAt, unread: Date.now() },
        }
      })
    } catch (error) {
      if (!lSilencioso) {
        set((state) => ({ loading: { ...state.loading, unread: false } }))
      }
      throw error
    }
  },

  initSocket: ({ socketManager, companyId, user }) => {
    if (!socketManager || !companyId || !user) return
    const state = get()
    if (state.socketInfo.companyId === companyId && state.socketConnected) return

    if (typeof state.socketCleanup === "function") {
      state.socketCleanup()
    }

    const oSocketGerenciado = socketManager.getSocket(companyId)
    const oFilasDoUserLogado = filasUsuarioPermitidasCalcular(user)
    const isUserPermitted = useUsuariosPermitidosStore.getState().isUserPermitted

    /** Salas necessárias para receber company-X-ticket e company-X-appMessage (igual TicketsList antigo). */
    const entrarNasSalas = () => {
      oSocketGerenciado.emit("joinTickets", "open")
      oSocketGerenciado.emit("joinTickets", "pending")
      oSocketGerenciado.emit("joinNotification")
    }
    oSocketGerenciado.on("ready", entrarNasSalas)

    const handleTicketEmpresa = (data) => {
      if (!data) return
      if (data.action === "delete") {
        get().removeTicket(data.ticketId || data.ticket?.id)
        return
      }

      const ticket = data.ticket
      if (!ticket?.id) return

      // Mescla com o existente para validação completa (mesclarTicket respeita IDs explícitos).
      const oTicketExistente = get().ticketsById?.[ticket.id]
      const ticketParaValidar = oTicketExistente ? mesclarTicket(oTicketExistente, ticket, user?.id) : ticket

      if (
        !usuarioTemAcessoAoTicket({
          ticket: ticketParaValidar,
          user,
          oFilasSelecionadas: [],
          oFilasDoUserLogado,
          profile: user?.profile,
          isUserPermitted,
        })
      ) {
        get().removeTicket(ticket.id)
        return
      }

      if (ticket.canalTipoId === canais.interno) {
        const isParticipant =
          ticketParaValidar.responsavelId === user.id ||
          ticketParaValidar.usuarioId === user.id ||
          (Array.isArray(ticketParaValidar.conversaParticipantes) &&
            ticketParaValidar.conversaParticipantes.some((p) => p.usuarioId === user.id))
        if (!isParticipant && user.profile !== "admin") {
          get().removeTicket(ticket.id)
          return
        }
      }

      if (data.action === "updateUnread") {
        get().updateTicketUnread(data.ticketId || ticket.id, ticketParaValidar)
      } else {
        get().upsertTicket(ticketParaValidar)
      }
    }

    const handleTicket = (data) => {
      if (data?.action !== "update" || !data.ticket?.id) return

      const ticket = data.ticket
      const oTicketExistente = get().ticketsById?.[ticket.id]
      const ticketParaValidar = oTicketExistente ? mesclarTicket(oTicketExistente, ticket, user?.id) : ticket

      if (
        !usuarioTemAcessoAoTicket({
          ticket: ticketParaValidar,
          user,
          oFilasSelecionadas: [],
          oFilasDoUserLogado,
          profile: user?.profile,
          isUserPermitted,
        })
      ) {
        get().removeTicket(ticket.id)
        return
      }

      get().upsertTicket(ticketParaValidar)
    }

    const handleFilaEmpresa = (data) => {
      if (data?.action === "delete" && data.queueId != null) {
        get().removerFilaDosTickets(data.queueId)
      }
    }

    const handleContatoEmpresa = (data) => {
      if (!data) return
      if (data.action === "update" || data.action === "create") {
        if (data.contact) {
          useContactsStore.getState().updateContact(data.contact)
        }
        return
      }
      if (data.action === "delete" && data.contactId != null) {
        useContactsStore.getState().deleteContact(+data.contactId)
      }
    }

    const handleMensagemAplicacao = (data) => {
      if (!data?.message) return
      if (data.action === "updateAck") return

      if (data.action === "create") {
        get().handleNewMessage(data.message)
        return
      }

      if (data.action === "update") {
        const t = data.message.ticket
        const ticketId = data.message.ticketId || t?.id
        if (!ticketId) return

        const cPreview = obterUltimaMensagemPreview(data.message)
        const cBody = typeof data.message.body === "string" ? data.message.body.trim() : ""
        const cTextoUtil = mensagemTextoSemAssinatura(cBody)
        const cMediaType = data.message.mediaType
        // Mídia com body vazio/só assinatura: prioriza preview (Imagem/Vídeo/Arquivo) em vez do body.
        const lUsarBodyTexto =
          Boolean(cTextoUtil) && !cTextoUtil.startsWith("_INFO_") && !cMediaType

        const oTicketExistente = get().ticketsById?.[ticketId]
        const ticketParaValidar = oTicketExistente
          ? mesclarTicket(oTicketExistente, t || {}, user?.id)
          : t

        if (
          !ticketParaValidar ||
          !usuarioTemAcessoAoTicket({
            ticket: ticketParaValidar,
            user,
            oFilasSelecionadas: [],
            oFilasDoUserLogado,
            profile: user?.profile,
            isUserPermitted,
          })
        ) {
          get().removeTicket(ticketId)
          return
        }

        get().upsertTicket({
          ...ticketParaValidar,
          id: ticketId,
          ultimaMensagem: lUsarBodyTexto ? data.message.body : cPreview || ticketParaValidar?.ultimaMensagem,
          ultimaMensagemData:
            data.message.updatedAt || data.message.createdAt || ticketParaValidar?.ultimaMensagemData,
        })
      }
    }

    oSocketGerenciado.on(`company-${companyId}-ticket`, handleTicketEmpresa)
    oSocketGerenciado.on("ticket", handleTicket)
    oSocketGerenciado.on(`company-${companyId}-queue`, handleFilaEmpresa)
    oSocketGerenciado.on(`company-${companyId}-contact`, handleContatoEmpresa)
    oSocketGerenciado.on(`company-${companyId}-appMessage`, handleMensagemAplicacao)

    const fLimpezaSocket = () => {
      // Não usar off("ready") no ManagedSocket: "ready" não fica em callbacks e o off quebraria o array.
      oSocketGerenciado.off(`company-${companyId}-ticket`, handleTicketEmpresa)
      oSocketGerenciado.off("ticket", handleTicket)
      oSocketGerenciado.off(`company-${companyId}-queue`, handleFilaEmpresa)
      oSocketGerenciado.off(`company-${companyId}-contact`, handleContatoEmpresa)
      oSocketGerenciado.off(`company-${companyId}-appMessage`, handleMensagemAplicacao)
      // ManagedSocket.disconnect emite leave* para cada join registrado (joinTickets / joinNotification)
      oSocketGerenciado.disconnect()
      set((s) => ({ socketConnected: false, socketCleanup: null, socketInfo: { ...s.socketInfo, companyId: null } }))
    }

    set({
      socketConnected: true,
      socketCleanup: fLimpezaSocket,
      socketInfo: { companyId, user },
    })
  },

  destroySocket: () => {
    const fLimpezaSocket = get().socketCleanup
    if (typeof fLimpezaSocket === "function") fLimpezaSocket()
  },
}))

export const selectOpenTickets = (state) =>
  state.openIds
    .map((id) => state.ticketsById[id])
    .filter(Boolean)
    .filter((ticket) => ticketPassaFiltrosLista(ticket, state))

export const selectPendingTickets = (state) =>
  state.pendingIds
    .map((id) => state.ticketsById[id])
    .filter(Boolean)
    .filter((ticket) => ticketPassaFiltrosLista(ticket, state, { ehSemResponsavel: true }))

export const conversasNaoLidasSelecionadas = (state) =>
  state.mNaoLidasIds
    .map((id) => state.ticketsById[id])
    .filter(Boolean)
    .filter((ticket) => ticketPassaFiltrosLista(ticket, state))
export const selectCounters = (state) => state.counters
export const selectTicketById = (id) => (state) => state.ticketsById[id]
